import { useQueryClient } from '@tanstack/react-query'
import { apiErrorText } from '../../../shared/api/errorText'
import {
  createProcurementResult,
  updateProcurementResult,
  deleteProcurementResult,
  patchItem,
} from '../../../shared/api/requestItems.api'
import type { ProcurementResult, SourceType } from '../../../shared/api/types'
import {
  convertQuoteToMatrixField,
  PRICE_FIELD_BY_MATRIX,
  toMatrixRates,
  type MatrixRates,
} from '../utils/convertQuoteToMatrixField'

/**
 * Логика работы с квотами матрицы прямо из ячейки таблицы позиций:
 * создание / правка / удаление квоты + поддержание значения ячейки.
 *
 * Значение ячейки матрицы (поле priceRF/targetPrice/…) трактуется как
 * «последняя внесённая цена»: любое создание или правка квоты этой матрицы
 * пишет в поле сконвертированную цену именно этой квоты (последнее действие
 * выигрывает). При удалении поле откатывается на цену самой свежей из
 * оставшихся квот матрицы (по createdAt), а если их не осталось — обнуляется.
 *
 * Курсы и матрица определяют, в какой валюте лежит поле (РФ — рубли, остальные
 * — юани), вся конвертация — в convertQuoteToMatrixField.
 *
 * Без курсов (`ratesReady === false`) создание и правка квоты не выполняются:
 * раньше в этом случае в поле позиции уезжала цена, посчитанная по выдуманным
 * курсам. Удалению курсы нужны только тогда, когда в матрице остаются другие
 * квоты с ценой (поле откатывается на их цену); если удаляется последняя —
 * поле просто обнуляется, конвертировать нечего. Вызывающие компоненты гасят
 * кнопки по тем же правилам, а гварды ниже — страховка на случай гонки
 * (курсы «протухли» по пути).
 */
export function useMatrixCellQuotes(
  requestId: string | undefined,
  rawRates: Partial<MatrixRates> | null | undefined,
) {
  const queryClient = useQueryClient()
  const rates = toMatrixRates(rawRates)

  /** Курсы обязаны быть загружены — иначе цену матрицы считать не по чему. */
  const requireRates = (): MatrixRates => {
    if (!rates) throw new Error('Курсы валют не загрузились — цену сохранить нельзя')
    return rates
  }

  const invalidate = (itemId: string) => {
    void queryClient.invalidateQueries({ queryKey: ['procurement-results', itemId] })
    void queryClient.invalidateQueries({ queryKey: ['requests', requestId] })
    void queryClient.invalidateQueries({ queryKey: ['request-items-by-request'] })
  }

  /** Записать «последнюю внесённую цену» в поле матрицы позиции. */
  const writeField = async (itemId: string, sourceType: SourceType, value: number | null) => {
    const field = PRICE_FIELD_BY_MATRIX[sourceType] ?? 'targetPrice'
    await patchItem(itemId, { [field]: value } as Parameters<typeof patchItem>[1])
  }

  const createQuote = async (
    itemId: string,
    sourceType: SourceType,
    data: { supplierName: string; price: number; currency: string; note?: string },
  ) => {
    const r = requireRates()
    await createProcurementResult(itemId, {
      supplierName: data.supplierName,
      price: data.price,
      currency: data.currency,
      sourceType,
      note: data.note,
    })
    // Поле ячейки = цена только что внесённой квоты.
    await writeField(
      itemId,
      sourceType,
      convertQuoteToMatrixField(data.price, data.currency, sourceType, r),
    )
    invalidate(itemId)
  }

  /**
   * Запись цены в ячейку ПОСЛЕ того, как квота на сервере уже изменена или
   * удалена. Упала запись — квоту назад не вернуть, поэтому сообщение прямо
   * говорит, что сохранилось, а что нет (№309), вместо общего «ошибка».
   */
  const syncFieldAfter = async (
    done: string,
    itemId: string,
    sourceType: SourceType,
    value: number | null,
  ) => {
    try {
      await writeField(itemId, sourceType, value)
    } catch (err) {
      throw new Error(
        `${done}, но цену в ячейке позиции обновить не удалось: ${apiErrorText(err, 'ошибка сохранения')}`,
      )
    }
  }

  const updateQuote = async (
    quoteId: string,
    itemId: string,
    sourceType: SourceType,
    data: { supplierName: string; price: number; currency: string; note: string | null },
  ) => {
    const r = requireRates()
    try {
      await updateProcurementResult(quoteId, {
        supplierName: data.supplierName,
        price: data.price,
        currency: data.currency,
        sourceType,
        note: data.note,
      })
      // Правка квоты = «последнее действие» → ячейка показывает её цену.
      await syncFieldAfter(
        'Квота изменена',
        itemId,
        sourceType,
        convertQuoteToMatrixField(data.price, data.currency, sourceType, r),
      )
    } finally {
      // Перезапрашиваем квоты и позицию и при успехе, и при сбое: при частичном
      // сбое (квота новая, ячейка старая) экран иначе расходится с базой, а при
      // отказе сервера (квоту успели изменить/удалить) — покажем актуальное.
      invalidate(itemId)
    }
  }

  /**
   * Удаляет квоту и пересчитывает значение ячейки. remaining — все квоты этой
   * матрицы ДО удаления (из открытого поповера); из них вычитаем удаляемую и
   * берём самую свежую (max createdAt) как новую «последнюю внесённую».
   */
  const deleteQuote = async (
    quoteId: string,
    itemId: string,
    sourceType: SourceType,
    remaining: ProcurementResult[],
  ) => {
    const rest = remaining.filter((q) => q.id !== quoteId && q.price != null)
    // Курс нужен только чтобы пересчитать ячейку на цену оставшейся квоты.
    // Проверяем до удаления, чтобы не остаться с удалённой квотой и старой ценой.
    const r = rest.length > 0 ? requireRates() : null
    try {
      await deleteProcurementResult(quoteId)
      if (r) {
        const latest = rest.reduce((a, b) => (a.createdAt >= b.createdAt ? a : b))
        await syncFieldAfter(
          'Квота удалена',
          itemId,
          sourceType,
          convertQuoteToMatrixField(latest.price as number, latest.currency, sourceType, r),
        )
      } else {
        await syncFieldAfter('Квота удалена', itemId, sourceType, null)
      }
    } finally {
      // Как и при правке: после сбоя экран сверяется с базой.
      invalidate(itemId)
    }
  }

  return { createQuote, updateQuote, deleteQuote, ratesReady: rates !== null }
}
